import React, { useMemo } from "react";
import { Box, Card, CardContent, Typography, useTheme } from "@mui/material";
import AccountBalanceWalletOutlinedIcon from "@mui/icons-material/AccountBalanceWalletOutlined";
import ArrowDropUpRoundedIcon from "@mui/icons-material/ArrowDropUpRounded";
import ArrowDropDownRoundedIcon from "@mui/icons-material/ArrowDropDownRounded";
import {
  getSignColor,
  formatCurrency,
  isValidNum,
} from "../../PerformanceDashboard/utility";
import {
  v2CardBg,
  v2CardBorder,
  v2LabelSx,
  v2Accent,
  v2AccentSoftBg,
} from "../../PerformanceDashboardV2/v2Theme";

/** Caret + % shown beside the card title. */
const TitlePct = ({ pct, theme, signed = true }) => {
  if (!isValidNum(pct)) return null;
  const pctNum = Number(pct);
  const display = Math.abs(pctNum).toFixed(2);
  const color = signed ? getSignColor(pctNum, theme) : "text.secondary";

  return (
    <Box
      sx={{
        display: "inline-flex",
        alignItems: "center",
        gap: 0.1,
        color,
        flexShrink: 0,
        ml: 0.75,
      }}
    >
      {signed &&
        (pctNum >= 0 ? (
          <ArrowDropUpRoundedIcon sx={{ fontSize: "1.2rem", ml: -0.4 }} />
        ) : (
          <ArrowDropDownRoundedIcon sx={{ fontSize: "1.2rem", ml: -0.4 }} />
        ))}
      <Typography
        component="span"
        sx={{
          fontWeight: 700,
          fontVariantNumeric: "tabular-nums",
          fontSize: "0.85rem",
          letterSpacing: "0.02em",
          lineHeight: 1,
          color: "inherit",
          textTransform: "none",
        }}
      >
        {display}%
      </Typography>
    </Box>
  );
};

const MetricTile = ({
  title,
  value,
  valueColor,
  pct,
  signedPct = true,
  theme,
  icon: Icon,
}) => {
  const isDark = theme.palette.mode === "dark";

  return (
    <Card
      variant="outlined"
      sx={{
        borderRadius: 2,
        height: "100%",
        width: "100%",
        borderColor: v2CardBorder(theme),
        backgroundColor: v2CardBg(theme),
        boxShadow: isDark ? "none" : "0 1px 3px rgba(0,0,0,0.06)",
        color: "text.primary",
      }}
    >
      <CardContent sx={{ py: 2, px: 2, "&:last-child": { pb: 2 } }}>
        <Box
          sx={{
            display: "flex",
            alignItems: "flex-start",
            justifyContent: "space-between",
            gap: 1,
          }}
        >
          <Box sx={{ minWidth: 0, flex: 1 }}>
            <Box
              sx={{
                display: "flex",
                alignItems: "center",
                flexWrap: "wrap",
                minWidth: 0,
              }}
            >
              <Typography
                variant="caption"
                sx={{
                  fontWeight: 600,
                  letterSpacing: "0.06em",
                  textTransform: "uppercase",
                  fontSize: "0.78rem",
                  ...v2LabelSx(theme),
                }}
              >
                {title}
              </Typography>
              <TitlePct pct={pct} theme={theme} signed={signedPct} />
            </Box>
            <Typography
              variant="h5"
              sx={{
                fontWeight: 700,
                mt: 0.75,
                fontVariantNumeric: "tabular-nums",
                lineHeight: 1.15,
                color: valueColor || "text.primary",
                fontSize: { xs: "1.15rem", md: "1.3rem" },
              }}
            >
              {value}
            </Typography>
          </Box>
          {Icon && (
            <Box
              sx={{
                flexShrink: 0,
                p: 1,
                borderRadius: 1.5,
                backgroundColor: v2AccentSoftBg(theme),
                color: v2Accent(theme),
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
              }}
            >
              <Icon sx={{ fontSize: 22 }} />
            </Box>
          )}
        </Box>
      </CardContent>
    </Card>
  );
};

const findRow = (totals, name) =>
  totals?.find((row) => row?.name?.toUpperCase() === name) || null;

const formatMoney = (value) => {
  const num = Number(value);
  if (!Number.isFinite(num)) return "—";
  return formatCurrency(num) || "—";
};

const aumDollarsFromPct = (pctOfAum, totalAumDollars) => {
  const pct = Number(pctOfAum);
  const total = Number(totalAumDollars);
  if (!Number.isFinite(pct) || !Number.isFinite(total)) return NaN;
  return (Math.abs(pct) / 100) * total;
};

/**
 * Hero KPIs: AUM (total / long / short) then performance (long / short / total).
 * % sits beside the title; dollars are the main value.
 */
const PortfolioHero = ({ totals = [] }) => {
  const theme = useTheme();

  const { longRow, shortRow, totalsRow } = useMemo(
    () => ({
      longRow: findRow(totals, "LONG"),
      shortRow: findRow(totals, "SHORT"),
      totalsRow: findRow(totals, "TOTALS"),
    }),
    [totals]
  );

  const totalAum = Number(totalsRow?.aumDollars);
  const longAumPct = Number(longRow?.totalsAUM);
  const shortAumPct = Number(shortRow?.totalsAUM);
  const longAumDollars = aumDollarsFromPct(longAumPct, totalAum);
  const shortAumDollars = aumDollarsFromPct(shortAumPct, totalAum);

  const longPl = Number(longRow?.totalsPl);
  const longPcm = Number(longRow?.totalsPcm);
  const shortPl = Number(shortRow?.totalsPl);
  const shortPcm = Number(shortRow?.totalsPcm);
  const totalPl = Number(totalsRow?.totalsPl);
  const totalPcm = Number(totalsRow?.totalsPcm);

  return (
    <Box
      sx={{
        width: "100%",
        display: "grid",
        gridTemplateColumns: {
          xs: "minmax(0, 1fr)",
          sm: "repeat(2, minmax(0, 1fr))",
          md: "repeat(3, minmax(0, 1fr))",
          lg: "repeat(6, minmax(0, 1fr))",
        },
        gap: 1.5,
        alignItems: "stretch",
      }}
    >
      <MetricTile
        theme={theme}
        title="Total AUM"
        value={Number.isFinite(totalAum) ? formatMoney(totalAum) : "—"}
        icon={AccountBalanceWalletOutlinedIcon}
      />
      <MetricTile
        theme={theme}
        title="Long AUM"
        value={Number.isFinite(longAumDollars) ? formatMoney(longAumDollars) : "—"}
        pct={Number.isFinite(longAumPct) ? Math.abs(longAumPct) : undefined}
        signedPct={false}
      />
      <MetricTile
        theme={theme}
        title="Short AUM"
        value={
          Number.isFinite(shortAumDollars) ? formatMoney(shortAumDollars) : "—"
        }
        pct={Number.isFinite(shortAumPct) ? Math.abs(shortAumPct) : undefined}
        signedPct={false}
      />
      <MetricTile
        theme={theme}
        title="Long P(L)"
        value={Number.isFinite(longPl) ? formatMoney(longPl) : "—"}
        valueColor={
          Number.isFinite(longPl) ? getSignColor(longPl, theme) : undefined
        }
        pct={longPcm}
      />
      <MetricTile
        theme={theme}
        title="Short P(L)"
        value={Number.isFinite(shortPl) ? formatMoney(shortPl) : "—"}
        valueColor={
          Number.isFinite(shortPl) ? getSignColor(shortPl, theme) : undefined
        }
        pct={shortPcm}
      />
      <MetricTile
        theme={theme}
        title="Total P(L)"
        value={Number.isFinite(totalPl) ? formatMoney(totalPl) : "—"}
        valueColor={
          Number.isFinite(totalPl) ? getSignColor(totalPl, theme) : undefined
        }
        pct={totalPcm}
      />
    </Box>
  );
};

export default PortfolioHero;
